import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import { isShellNavItemActive, ShellNavItem } from '../shell-nav.config';

@Component({
  selector: 'app-link-list-item',
  templateUrl: './link-list-item.component.html',
  styleUrls: ['./link-list-item.component.scss']
})
export class LinkListItemComponent implements OnChanges {
  @Input() link: ShellNavItem | undefined;

  @Input() variant: 'topbar' | 'drawer' = 'topbar';

  @Input() orderTooltip = '';

  @Output() itemNavigate = new EventEmitter<void>();

  expanded = false;
  openGroupedChildKey = '';
  openGroupedChildTopOffset = '0px';

  constructor(private router: Router) {}

  ngOnChanges(): void {
    if (this.variant === 'drawer' && this.hasChildren && this.isActive) {
      this.expanded = true;
    }
  }

  get hasChildren() {
    return Boolean(this.link?.children?.length);
  }

  get accentName() {
    return this.link?.accent || '';
  }

  get isActive() {
    return Boolean(this.link && isShellNavItemActive(this.link, this.router.url));
  }

  get tooltipText() {
    if (this.link?.meta?.tooltip === 'orders-status') {
      return this.orderTooltip;
    }

    return '';
  }

  trackByPath(_: number, link: ShellNavItem) {
    return `${link.path}:${link.label}`;
  }

  hasNestedChildren(link: ShellNavItem) {
    return Boolean(link.children?.length);
  }

  isItemActive(link: ShellNavItem) {
    return isShellNavItemActive(link, this.router.url);
  }

  isCreatePathActive(link: ShellNavItem) {
    if (!link.createPath) {
      return false;
    }

    const currentUrl = this.normalizePath(this.router.url);
    const createPath = this.normalizePath(link.createPath);

    return currentUrl === createPath || currentUrl.startsWith(`${createPath}/`);
  }

  isItemOrCreateActive(link: ShellNavItem) {
    return this.isItemActive(link) || this.isCreatePathActive(link);
  }

  isGroupedChildOpen(link: ShellNavItem) {
    return this.openGroupedChildKey === this.getGroupedChildKey(link);
  }

  toggleExpanded() {
    this.expanded = !this.expanded;
  }

  handleNavigate() {
    this.closeGroupedChild();
    this.itemNavigate.emit();
  }

  navigateToCreate(path: string, event: Event, topbarMenuTrigger?: MatMenuTrigger) {
    event.stopPropagation();
    this.closeGroupedChild();
    topbarMenuTrigger?.closeMenu();
    this.itemNavigate.emit();
    void this.router.navigateByUrl(path);
  }

  openGroupedChild(link: ShellNavItem, triggerElement: MatMenuItem) {
    this.openGroupedChildKey = this.getGroupedChildKey(link);
    this.openGroupedChildTopOffset = '0px';

    window.requestAnimationFrame(() => {
      const viewportPadding = 16;
      const triggerHost = triggerElement._getHostElement();
      const triggerRect = triggerHost.getBoundingClientRect();
      const flyoutElement = triggerHost.parentElement?.querySelector(
        '.shellMenuFlyout'
      ) as HTMLElement | null;

      if (!flyoutElement) {
        return;
      }

      const flyoutRect = flyoutElement.getBoundingClientRect();
      const preferredTopOffset = Math.min(
        0,
        Math.floor(window.innerHeight - viewportPadding - (triggerRect.top + flyoutRect.height))
      );
      const minTopOffset = Math.floor(viewportPadding - triggerRect.top);

      this.openGroupedChildTopOffset = `${Math.max(minTopOffset, preferredTopOffset)}px`;
    });
  }

  toggleGroupedChild(link: ShellNavItem, event: Event, triggerElement: MatMenuItem) {
    event.stopPropagation();

    if (this.isGroupedChildOpen(link)) {
      this.closeGroupedChild(link);
      return;
    }

    this.openGroupedChild(link, triggerElement);
  }

  closeGroupedChild(link?: ShellNavItem) {
    if (link && !this.isGroupedChildOpen(link)) {
      return;
    }

    this.openGroupedChildKey = '';
  }

  private getGroupedChildKey(link: ShellNavItem) {
    return `${this.normalizePath(link.path)}:${link.label}`;
  }

  private normalizePath(path: string) {
    return String(path || '')
      .split('?')[0]
      .split('#')[0]
      .replace(/\/+$/, '');
  }
}
